import { useCallback, useState } from 'react';
import { usesStoreBilling } from '@/lib/appDistribution';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { useAuthStore, useToastStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';

export function canRestoreIapPurchases() {
  return usesStoreBilling();
}

export default function useRestoreIapPurchases() {
  const { t } = useTranslation();
  const { iap } = useGabPurchase();
  const fetchUser = useAuthStore((state) => state.fetchUser);
  const fetchUserCredits = useAuthStore((state) => state.fetchUserCredits);
  const { addToast } = useToastStore((state) => state.actions);
  const [isRestoring, setIsRestoring] = useState(false);

  const restorePurchases = useCallback(async () => {
    if (isRestoring) return { success: false, error: 'Restore already in progress' };
    if (!iap?.restorePurchases) {
      addToast(t('settingsMobile.restorePurchasesFailed'), 'error');
      return { success: false, error: 'Store is not available' };
    }

    setIsRestoring(true);
    iap.clearError?.();
    try {
      const result = await iap.restorePurchases();
      if (!result?.success) {
        addToast(result?.pending
          ? 'Your purchase is still being verified. Please try Restore again shortly; do not purchase again.'
          : result?.error || t('settingsMobile.restorePurchasesFailed'), result?.pending ? 'info' : 'error');
        return result || { success: false };
      }

      try { await Promise.all([fetchUser?.(), fetchUserCredits?.()].filter(Boolean)); }
      catch (_) {
        addToast('Your purchases were checked, but your account could not refresh. Please try Restore again shortly.', 'info');
        return result;
      }
      const isNowPlus = !!useAuthStore.getState().user?.flags?.isPlus;
      if (result?.message === 'No purchases to restore' && !isNowPlus) {
        addToast(t('settingsMobile.restorePurchasesNone'), 'info');
      } else {
        addToast(t('settingsMobile.restorePurchasesSuccess'), 'success');
      }
      return result;
    } catch (error) {
      addToast(error?.message || t('settingsMobile.restorePurchasesFailed'), 'error');
      return { success: false, error: error?.message };
    } finally {
      setIsRestoring(false);
    }
  }, [addToast, fetchUser, fetchUserCredits, iap, isRestoring, t]);

  return {
    canRestore: canRestoreIapPurchases(),
    isRestoring,
    restorePurchases,
  };
}
